import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useConfirm } from './ConfirmDialog';
import './Assets.css';

/**
 * Settings → Assets → Instructions: the wording IT and contractors send.
 *
 * Each template is a title, numbered steps for Windows and for Mac, the
 * finding that suggests it, the ways it is sent, and whether a contractor
 * may send it. {{placeholders}} such as {{count}} or {{volume}} are filled
 * from the finding when it is suggested.
 *
 * The server refuses wording that tells somebody to switch off a security
 * control, type a password or run a command, and this screen shows that
 * refusal as it came - the line is held where the words are written.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const errorOf = (e, fallback) => e.response?.data?.error || fallback;

const FINDING_WORDS = {
  updates_pending: 'Updates waiting, or a restart to finish them',
  long_uptime: 'Not restarted for over 14 days',
  disk_full: 'A disk over 90% full',
  process_hog: 'A program using most of the processor or memory',
  device_problem: 'Hardware with a problem code',
  battery_worn: 'Battery under 60% of its design',
  encryption_off: 'Disk not encrypted',
  network_unreachable: 'Router or Jarvis cannot be reached',
  many_startup: 'Many programs starting with the machine'
};
const PLACEHOLDERS = '{{count}} {{days}} {{volume}} {{used_pct}} {{process}} {{device}} {{health_pct}} {{items}}';
const CHANNELS = [['comment', 'Ticket comment'], ['bell', 'Bell'], ['agent', 'Notice on the computer']];

const blank = { title: '', finding: '', steps_windows: '', steps_mac: '', channels: ['comment', 'bell', 'agent'],
  contractor_may_send: false, active: true };
// A column heading read out but not shown.
const SR = { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' };

export default function InstructionTemplates() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');
  const [overdue, setOverdue] = useState('');
  const [confirm, confirmDialog] = useConfirm();

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/instruction-templates`);
      setData(r.data);
      setOverdue(String(r.data.overdue_days ?? 3));
    } catch (e) {
      setError(errorOf(e, 'Could not load the instructions.'));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (error && !data) return <p className="as-note" data-tone="warning">{error}</p>;
  if (!data) return <p className="as-quiet">Loading…</p>;

  const edit = (t) => {
    setError('');
    setEditing(t ? { ...t, finding: t.finding || '', steps_windows: (t.steps_windows || []).join('\n'),
      steps_mac: (t.steps_mac || []).join('\n') } : { ...blank });
  };

  const save = async () => {
    setError('');
    const lines = (s) => s.split('\n').map((x) => x.trim()).filter(Boolean);
    const body = { ...editing, finding: editing.finding || null,
      steps_windows: lines(editing.steps_windows), steps_mac: lines(editing.steps_mac) };
    try {
      if (editing.id) await axios.put(`${API_BASE}/instruction-templates/${editing.id}`, body);
      else await axios.post(`${API_BASE}/instruction-templates`, body);
      setEditing(null);
      await load();
    } catch (e) {
      setError(errorOf(e, 'Could not save it.'));
    }
  };

  const remove = async (t) => {
    setError('');
    // Sent at least once, the server keeps it and switches it off instead -
    // the "In use" box brings it back - so only an outright delete asks.
    if (!t.times_sent && !(await confirm({ title: `Delete "${t.title || 'this instruction'}"?`,
      message: 'It has never been sent, so it goes for good.', confirmLabel: 'Delete', danger: true }))) return;
    try {
      await axios.delete(`${API_BASE}/instruction-templates/${t.id}`);
      setEditing(null);
      await load();
    } catch (e) {
      setError(errorOf(e, 'Could not remove it.'));
    }
  };

  const set = (k, v) => setEditing((d) => ({ ...d, [k]: v }));

  return (
    <div className="as-section">
      <p className="as-quiet">
        What IT and contractors can send somebody to do on their computer. Jarvis suggests one when a diagnostic
        finds its problem, and confirms it worked when the next check no longer finds it.
      </p>
      {error && <p className="as-error" role="alert">{error}</p>}

      <div className="ar-rule" id="s-overdue">
        <span className="ar-label">Tell the sender after</span>
        <div className="as-inline-form">
          <div className="form-group" style={{ flex: '0 1 8rem' }}>
            <label htmlFor="in-overdue">Days not done</label>
            <input id="in-overdue" type="number" min={1} max={60} value={overdue} onChange={(e) => setOverdue(e.target.value)} />
          </div>
          <button type="button" className="btn btn-secondary" disabled={String(data.overdue_days) === overdue}
            onClick={async () => {
              try {
                await axios.put(`${API_BASE}/app-settings/assets-instructions-overdue-days`, { value: Number(overdue) });
                await load();
              } catch (e) { setError(errorOf(e, 'Could not save it.')); }
            }}>Save</button>
        </div>
      </div>

      {!editing && (
        <div className="as-actions">
          <button type="button" className="btn btn-primary" onClick={() => edit(null)}>New instruction</button>
        </div>
      )}

      {editing && (
        <div className="as-panel" data-testid="template-editor">
          <div className="form-group">
            <label htmlFor="tpl-title">Title</label>
            <input id="tpl-title" value={editing.title} maxLength={120} onChange={(e) => set('title', e.target.value)} />
          </div>
          <div className="form-group">
            <label htmlFor="tpl-finding">Suggested when the device shows</label>
            <select id="tpl-finding" value={editing.finding} onChange={(e) => set('finding', e.target.value)}>
              <option value="">Nothing - sent by hand only</option>
              {data.findings.map((f) => <option key={f} value={f}>{FINDING_WORDS[f] || f}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="tpl-win">Steps on Windows, one per line</label>
            <textarea id="tpl-win" rows={5} value={editing.steps_windows} onChange={(e) => set('steps_windows', e.target.value)} />
          </div>
          <div className="form-group">
            <label htmlFor="tpl-mac">Steps on a Mac, one per line</label>
            <textarea id="tpl-mac" rows={5} value={editing.steps_mac} onChange={(e) => set('steps_mac', e.target.value)} />
          </div>
          <p className="as-quiet">{`Filled in from the finding: ${PLACEHOLDERS}`}</p>
          <fieldset className="as-instr-channels">
            <legend>Sent as</legend>
            {CHANNELS.map(([c, w]) => (
              <label key={c} className="as-check">
                <input type="checkbox" checked={editing.channels.includes(c)}
                  onChange={() => set('channels', editing.channels.includes(c)
                    ? editing.channels.filter((x) => x !== c) : [...editing.channels, c])} />
                <span>{w}</span>
              </label>
            ))}
          </fieldset>
          <label className="as-check">
            <input type="checkbox" checked={editing.contractor_may_send}
              onChange={() => set('contractor_may_send', !editing.contractor_may_send)} />
            <span>The contractor on a ticket may send this</span>
          </label>
          <label className="as-check">
            <input type="checkbox" checked={editing.active} onChange={() => set('active', !editing.active)} />
            <span>In use</span>
          </label>
          <div className="as-actions" style={{ marginTop: '0.75rem' }}>
            <button type="button" className="btn btn-primary" onClick={save}>Save</button>
            <button type="button" className="btn btn-secondary" onClick={() => { setEditing(null); setError(''); }}>Cancel</button>
            {editing.id && (
              <button type="button" className="btn btn-plain" onClick={() => remove(editing)}>
                {editing.times_sent ? 'Switch off' : 'Delete'}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="as-table-wrap">
        <table className="as-table">
          <thead><tr><th>Instruction</th><th>Suggested when</th><th>Contractor</th><th>Sent</th><th><span style={SR}>Actions</span></th></tr></thead>
          <tbody>
            {/* The row opens the template as a convenience; the Edit button
                is the way a keyboard or a screen reader gets there. */}
            {data.templates.map((t) => (
              <tr key={t.id} onClick={() => edit(t)} data-off={String(!t.active)}>
                <td data-label="Instruction">{t.title}{!t.active && <span className="as-sub">Switched off</span>}</td>
                <td data-label="Suggested when">{FINDING_WORDS[t.finding] || '—'}</td>
                <td data-label="Contractor">{t.contractor_may_send ? 'Yes' : 'No'}</td>
                <td data-label="Sent">{t.times_sent}</td>
                <td data-label="">
                  <button type="button" className="btn btn-plain btn-small" aria-label={`Edit ${t.title}`}
                    onClick={(e) => { e.stopPropagation(); edit(t); }}>
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {confirmDialog}
    </div>
  );
}
